import { filenameFromDisposition, saveBlob } from '../lib/downloadFile';
import type { NotesCapacity } from '../pages/lite/notesGovernor';
import type { SendStatus } from '../pages/lite/sendHold';
import { api } from './api';
import type {
  InspectionResponse,
  InspectionCreate,
  InspectionUpdate,
  InspectionSubmit,
  InspectionDraftResponse,
  InspectionDraftCreate,
  InspectionDraftUpdate,
  InspectionFormConfig,
  PaginatedResponse,
  ScheduledInspection,
} from '../types/api';

type InspectionListParams = {
  site_id?: string;
  status?: string;
  inspector_id?: string;
  conducted_after?: string;
  conducted_before?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  size?: number;
  include_details?: boolean;
  skip_count?: boolean;
};

// Internal draft type with camelCase for frontend use
type InspectionDraft = {
  id: string;
  siteId: string;
  data: Record<string, unknown>;
  lastSavedAt: string;
};

export type InspectionStatsPayload = {
  total: number;
  approved: number;
  submitted: number;
  scheduled: number;
  draft: number;
  rejected: number;
  completed?: number;
  violations_count: number;
};

/**
 * The Reports page tiles. "Completed" is every finished report: approved,
 * submitted, and the legacy import's `completed` status — leaving the last
 * out showed 18k where prod holds 378k and read as "too low" (Jim, Aug 2026).
 */
export function summarizeInspectionStats(stats: InspectionStatsPayload) {
  return {
    totalInspections: stats.total,
    pendingInspections: stats.scheduled + stats.draft,
    completedInspections:
      stats.approved + stats.submitted + (stats.completed ?? 0),
    violationsCount: stats.violations_count,
  };
}

class InspectionService {
  async getFormConfig(
    siteId: string,
    inspectionType: string
  ): Promise<InspectionFormConfig> {
    const response = await api.get('/inspections/form-config', {
      params: { site_id: siteId, inspection_type: inspectionType },
    });
    return response.data as InspectionFormConfig;
  }

  async getInspection(inspectionId: string): Promise<InspectionResponse> {
    const response = await api.get(`/inspections/${inspectionId}`);
    return response.data as InspectionResponse;
  }

  async createInspection(data: InspectionCreate): Promise<InspectionResponse> {
    // Trailing slash required - FastAPI redirect loses auth header without it
    const response = await api.post('/inspections/', data);
    return response.data as InspectionResponse;
  }

  /**
   * Re-submit an inspection the inspector already reviewed (Edit → Review
   * Report). Replaces findings + checklist answers on the SAME inspection so
   * the flow never creates a second one for the same visit.
   */
  async reviseInspection(
    inspectionId: string,
    data: InspectionCreate
  ): Promise<InspectionResponse> {
    const response = await api.post(
      `/inspections/${inspectionId}/revise`,
      data
    );
    return response.data as InspectionResponse;
  }

  async updateInspection(
    inspectionId: string,
    data: InspectionUpdate
  ): Promise<InspectionResponse> {
    const response = await api.patch(`/inspections/${inspectionId}`, data);
    return response.data as InspectionResponse;
  }

  async updateInspectionDate(
    inspectionId: string,
    conductedDate: string
  ): Promise<{
    success: boolean;
    inspection_id: string;
    conducted_date: string;
  }> {
    const response = await api.patch<{
      success: boolean;
      inspection_id: string;
      conducted_date: string;
    }>(`/inspections/${inspectionId}/date`, {
      conducted_date: conductedDate,
    });
    return response.data;
  }

  async submitInspection(
    inspectionId: string,
    data: InspectionSubmit
  ): Promise<InspectionResponse> {
    const response = await api.post(
      `/inspections/${inspectionId}/submit`,
      data
    );
    return response.data as InspectionResponse;
  }

  async getDrafts(): Promise<InspectionDraft[]> {
    const response = await api.get('/inspections/drafts');
    const drafts = response.data as InspectionDraftResponse[];

    // Transform to frontend format
    return drafts.map((draft) => ({
      id: draft.id,
      siteId: draft.site_id,
      data: draft.data as Record<string, unknown>,
      lastSavedAt: draft.last_saved_at,
    }));
  }

  async getDraft(draftId: string): Promise<InspectionDraftResponse> {
    const response = await api.get(`/inspections/drafts/${draftId}`);
    return response.data as InspectionDraftResponse;
  }

  async createDraft(
    data: InspectionDraftCreate
  ): Promise<InspectionDraftResponse> {
    const response = await api.post('/inspections/drafts', data);
    return response.data as InspectionDraftResponse;
  }

  async updateDraft(
    draftId: string,
    data: InspectionDraftUpdate
  ): Promise<InspectionDraftResponse> {
    const response = await api.patch(`/inspections/drafts/${draftId}`, data);
    return response.data as InspectionDraftResponse;
  }

  async deleteDraft(draftId: string): Promise<void> {
    await api.delete(`/inspections/drafts/${draftId}`);
  }

  async approveInspection(inspectionId: string): Promise<InspectionResponse> {
    const response = await api.post(`/inspections/${inspectionId}/approve`);
    return response.data as InspectionResponse;
  }

  async rejectInspection(
    inspectionId: string,
    reason: string
  ): Promise<InspectionResponse> {
    const response = await api.post(
      `/inspections/${inspectionId}/reject`,
      null,
      {
        params: { reason },
      }
    );
    return response.data as InspectionResponse;
  }

  async listInspections(
    params?: InspectionListParams
  ): Promise<PaginatedResponse<InspectionResponse>> {
    const response = await api.get('/inspections/', { params });
    return response.data as PaginatedResponse<InspectionResponse>;
  }

  async getScheduledInspections(): Promise<{ items: ScheduledInspection[] }> {
    // Fetch scheduled inspections
    const response = await api.get('/inspections/', {
      params: { status: 'scheduled', size: 50 },
    });
    const data = response.data as PaginatedResponse<InspectionResponse>;

    // Transform API response to schedule format
    const items: ScheduledInspection[] = data.items.map((inspection) => {
      const scheduledDate = new Date(inspection.scheduled_date || '');
      return {
        id: inspection.id,
        siteId: inspection.site_id,
        siteName: inspection.site_name || 'Site',
        location: inspection.site_location || '',
        date: scheduledDate.toISOString().split('T')[0],
        time: scheduledDate.toTimeString().slice(0, 5),
        type: inspection.type || 'routine',
        reason: this.getInspectionReason(inspection.type),
        latitude: inspection.site_latitude ?? undefined,
        longitude: inspection.site_longitude ?? undefined,
      };
    });

    return { items };
  }

  private getInspectionReason(type: string): string {
    switch (type) {
      case 'routine':
        return 'Regular scheduled inspection';
      case 'rainfall':
        return 'Rain event triggered inspection';
      case 'pre_construction':
        return 'Pre-construction inspection';
      case 'final':
        return 'Final site inspection';
      default:
        return 'Scheduled inspection';
    }
  }

  /**
   * Get lightweight inspection summary data for reports performance
   * Returns aggregated stats instead of full inspection objects
   * @param limit - Maximum inspections to analyze for stats
   * @returns Inspection summary statistics
   */
  async getInspectionSummary(): Promise<{
    totalInspections: number;
    pendingInspections: number;
    completedInspections: number;
    violationsCount: number;
  }> {
    const response = await api.get<{
      total: number;
      approved: number;
      submitted: number;
      scheduled: number;
      draft: number;
      rejected: number;
      // Legacy-imported visits (the bulk of history) — a finished report
      // is approved, submitted or legacy-completed.
      completed?: number;
      violations_count: number;
    }>('/inspections/stats');

    return summarizeInspectionStats(response.data);
  }

  /**
   * Generate the SWPPP PDF for a submitted inspection (slow: up to 2 min).
   * Owns what was previously inlined in useReportGeneration +
   * LiteInspectionFlowPage (overlap-audit 4.1: reporting had no service).
   */
  async generateReport(inspectionId: string) {
    const response = await api.post<{
      success: boolean;
      report_url: string;
      filename: string;
    }>(`/inspections/${inspectionId}/generate-report`, {}, { timeout: 120000 });
    return response.data;
  }

  /** Generate + email the report to site contacts (slow: up to 2 min). */
  async approveAndSendReport(inspectionId: string) {
    const response = await api.post<{
      success: boolean;
      message: string;
      report_url: string;
      email_results: {
        sent_count: number;
        failed_count: number;
        recipients: Array<{ email: string; name: string; status: string }>;
      };
      no_contacts: boolean;
      /** JIM-0922-01: who was asked to sign (or auto-signed), if the site takes client signatures. */
      client_signature?: {
        status: string;
        signer_name: string;
        signer_email: string;
        auto_applied?: boolean;
      } | null;
    }>(
      `/inspections/${inspectionId}/approve-and-send`,
      {},
      { timeout: 120000 }
    );
    return response.data;
  }

  /**
   * Send to Client from EnviroLite (JIM-1003-05): the report is queued and the
   * server emails it when the ten-minute hold runs out. Nothing is emailed by
   * this call unless the hold is switched off, in which case the response is
   * the approve-and-send receipt with `queued: false`.
   */
  async queueSend(inspectionId: string) {
    const response = await api.post<
      | ({ queued: true } & SendStatus)
      | ({ queued: false } & Awaited<
          ReturnType<InspectionService['approveAndSendReport']>
        >)
    >(`/inspections/${inspectionId}/queue-send`, {}, { timeout: 120000 });
    return response.data;
  }

  /** Stop the clock on a held report. Rejects (409) once the send has started. */
  async cancelSend(inspectionId: string) {
    const response = await api.post<{ cancelled: boolean } & SendStatus>(
      `/inspections/${inspectionId}/cancel-send`,
      {}
    );
    return response.data;
  }

  /** Jim 10/30: take a never-sent report off the Unsent Reports list. Nothing is emailed. */
  async dismissUnsent(inspectionId: string) {
    const response = await api.post<{
      dismissed: boolean;
      inspection_id: string;
      dismissed_at: string;
    }>(`/inspections/${inspectionId}/dismiss-unsent`, {});
    return response.data;
  }

  /** Where a report is on its way to the client. */
  async getSendStatus(inspectionId: string) {
    const response = await api.get<SendStatus>(
      `/inspections/${inspectionId}/send-status`
    );
    return response.data;
  }

  /** Fetch the presigned report URL for an inspection, if one exists. */
  /**
   * The report's bytes, through the API: the S3 bucket has no CORS rules
   * for the web origin, so pdf.js cannot fetch a presigned URL itself.
   */
  async getReportBytes(inspectionId: string): Promise<Uint8Array> {
    const response = await api.get(`/inspections/${inspectionId}/report.pdf`, {
      responseType: 'arraybuffer',
    });
    return new Uint8Array(response.data as ArrayBuffer);
  }

  /**
   * Save the report PDF to the device (JIM-0908-12). The bytes come through
   * the API (bearer token), so this fetches them and hands the viewer a file
   * named the way the server suggests (`<site>_<date>.pdf`).
   */
  async downloadReportPdf(inspectionId: string): Promise<void> {
    const response = await api.get(
      `/inspections/${inspectionId}/report.pdf?download=1`,
      { responseType: 'arraybuffer' }
    );
    const filename = filenameFromDisposition(
      response.headers?.['content-disposition'] as string | undefined,
      `inspection-report-${inspectionId.slice(0, 8)}.pdf`
    );
    saveBlob(response.data as ArrayBuffer, filename, 'application/pdf');
  }

  /**
   * Print the report PDF (JIM-0919-05, Jim 2026-09-19: "how are we going to
   * print this thing?"). The bytes come through the API with the bearer
   * token, so: open the tab synchronously (popup blockers only allow that
   * inside the tap), fetch the PDF, then point the tab at a blob URL — the
   * browser's PDF viewer offers Print on a desktop and Share → Print on a
   * phone. If no tab could be opened, fall back to saving the file.
   */
  async printReportPdf(inspectionId: string): Promise<void> {
    const tab =
      typeof window !== 'undefined' ? window.open('', '_blank') : null;
    if (tab) {
      try {
        tab.document.title = 'Loading report…';
      } catch {
        /* cross-origin about:blank in some WebViews — the title is cosmetic */
      }
    }
    try {
      const response = await api.get(
        `/inspections/${inspectionId}/report.pdf`,
        {
          responseType: 'arraybuffer',
        }
      );
      const blob = new Blob([response.data as ArrayBuffer], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      if (tab && !tab.closed) {
        tab.location.href = url;
        // Leave the URL alive long enough for the viewer to load it.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      URL.revokeObjectURL(url);
      await this.downloadReportPdf(inspectionId);
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      throw err;
    }
  }

  async getReportUrl(inspectionId: string) {
    const response = await api.get<{
      has_report: boolean;
      report_url?: string;
    }>(`/inspections/${inspectionId}/report-url`);
    return response.data;
  }

  /**
   * Who Send to Client will email for this inspection — resolved by the API
   * exactly the way approve-and-send resolves it. Shown in the confirm step
   * before anything is sent (Jim 2026-09-04).
   */
  /**
   * Throw away a draft the inspector never finished (JIM-0919-11): the
   * "Needs a report" row on the dashboard, when the site was only opened to
   * look. Own drafts and admins only; anything with a report is refused.
   */
  async discardDraftInspection(inspectionId: string): Promise<void> {
    await api.delete(`/inspections/${inspectionId}/draft`);
  }

  /**
   * How much the page-one notes box holds for a site with this many stages
   * (JIM-0919-03): the Lite notes box sizes itself to `lines` and stops
   * there, so notes never spill to page 2.
   */
  async getNotesCapacity(
    stageCount: number,
    finalInspection: boolean
  ): Promise<NotesCapacity> {
    const response = await api.get<NotesCapacity>(
      '/inspections/notes-capacity',
      { params: { stage_count: stageCount, final_inspection: finalInspection } }
    );
    return response.data;
  }

  /**
   * JIM-1007-11: email the stored report again — to the site's people, or
   * anyone by address. Nobody is asked to sign again.
   */
  async sendReportAgain(
    inspectionId: string,
    recipients: Array<{ email: string; name?: string }>
  ): Promise<SendAgainResult> {
    const response = await api.post<SendAgainResult>(
      `/inspections/${inspectionId}/send-again`,
      { recipients },
      { timeout: 120000 }
    );
    return response.data;
  }

  async getReportRecipients(inspectionId: string): Promise<ReportRecipients> {
    const response = await api.get<ReportRecipients>(
      `/inspections/${inspectionId}/report-recipients`
    );
    return response.data;
  }

  /**
   * EP-JURISFORM: uploaded jurisdiction-mandated form info + send preview
   * (presigned URL, notify-enabled recipients, CA count). Returns null when
   * no form has been uploaded (API 404s by contract).
   */
  async getJurisdictionReport(
    inspectionId: string
  ): Promise<JurisdictionReportInfo | null> {
    try {
      const response = await api.get<JurisdictionReportInfo>(
        `/inspections/${inspectionId}/jurisdiction-report`
      );
      return response.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return null;
      throw err;
    }
  }

  /** EP-JURISFORM: upload the completed jurisdiction form PDF. */
  async uploadJurisdictionReport(
    inspectionId: string,
    file: File,
    onProgress?: (percent: number) => void
  ) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{
      success: boolean;
      report_url: string | null;
      filename: string;
    }>(`/inspections/${inspectionId}/jurisdiction-report`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: (event: { loaded: number; total?: number }) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });
    return response.data;
  }

  /**
   * EP-JURISFORM: email the jurisdiction form + generated CA log to the
   * site's notify-enabled contacts (CA details listed in the body).
   */
  async sendJurisdictionReport(inspectionId: string) {
    const response = await api.post<{
      success: boolean;
      message: string;
      sent_count: number;
      failed_count: number;
      recipients: Array<{ email: string; name: string; status: string }>;
      ca_count: number;
    }>(
      `/inspections/${inspectionId}/send-jurisdiction-report`,
      {},
      { timeout: 120000 }
    );
    return response.data;
  }
}

export interface SendAgainResult {
  success: boolean;
  sent_count: number;
  failed_count: number;
  recipients: Array<{ email: string; name: string; status: string }>;
}

export interface ReportRecipients {
  inspection_id: string;
  site_name: string | null;
  already_sent: boolean;
  recipients: Array<{ email: string; name: string }>;
  // R11: the send applies the client's on-file signature itself.
  client_auto_sign?: { signer_name: string; since: string | null } | null;
}

export interface JurisdictionReportInfo {
  report_url: string;
  filename: string | null;
  uploaded_at: string | null;
  recipients: Array<{ email: string; name: string }>;
  ca_count: number;
}

export const inspectionService = new InspectionService();
